// The formula behind a figure of the events table (TX-TSK-0126), as LaTeX for KaTeX to typeset.
//
// The engine sends each amount's formula as named factors; this only writes them out - every
// input over the brace that names it, multiplied or divided out to the figure. No figure is
// calculated here: each one printed is a value the API sent.

import { toPercent } from './inputs.js';

const grouped = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, '{,}');

// A decimal string, grouped in thousands, to at most `places` decimals without trailing zeros.
export function number(value, places = 3) {
  const [whole, fraction = ''] = Number(value).toFixed(places).replace(/-/, '').split('.');
  const kept = fraction.replace(/0+$/, '');
  return `${grouped(whole)}${kept ? `.${kept}` : ''}`;
}

const TEXT_SPECIALS = /[\\{}$&#^_%~]/g;
export const text = (words) => `\\text{${words.replace(TEXT_SPECIALS, (c) => `\\${c}`)}}`;

const symbol = (currency) => (currency === 'USD' ? '\\$' : `${text(currency)}\\,`);

// Money to the cent, unsigned; the sign is written by whatever joins it.
export function texMoney(value, currency) {
  const [whole, cents] = Math.abs(Number(value)).toFixed(2).split('.');
  return `${symbol(currency)}${grouped(whole)}.${cents}`;
}

// A rate keeps the precision it was given: USD 18.125/MT is not USD 18.13/MT.
const rate = (value, currency) => {
  const cents = Number(value).toFixed(2);
  return Number(cents) === Number(value)
    ? texMoney(value, currency)
    : `${symbol(currency)}${number(value, 6)}`;
};

const plural = (value, one) => `${number(value)}\\,${text(Number(value) === 1 ? one : `${one}s`)}`;

const UNITS = {
  MT: (v) => `${number(v)}\\,${text('MT')}`,
  MONEY: (v, c) => texMoney(v, c),
  MONEY_PER_MT: (v, c) => `${rate(v, c)}/${text('MT')}`,
  MONEY_PER_UNIT: (v, c) => `${rate(v, c)}/${text('unit')}`,
  MONEY_PER_MT_PER_DAY: (v, c) => `${rate(v, c)}/${text('MT')}/${text('day')}`,
  PERCENT: (v) => `${toPercent(v)}\\%`,
  DAYS: (v) => plural(v, 'day'),
  UNITS: (v) => plural(v, 'unit'),
};

export const braced = (value, label) => `\\underbrace{${value}}_{${text(label)}}`;

export const factorTex = (factor, currency) =>
  braced(UNITS[factor.unit](factor.value, currency), factor.label);

const TIMES = ' \\times ';

// A product of its factors. A divisor divides the factor before it, as one fraction - "the PO's
// quantity over the quantity supplied", "the days funded over the days in a year" - which says
// what the division is for.
export function productTex(product, currency) {
  const terms = [];
  for (const factor of product.factors) {
    const tex = factorTex(factor, currency);
    if (factor.divides && terms.length) terms.push(`\\dfrac{${terms.pop()}}{${tex}}`);
    else terms.push(tex);
  }
  return terms.join(TIMES);
}

export const result = (tex) => `\\boxed{\\mathbf{${tex}}}`;

// The unsigned amount of an event: its product equal to the figure, or, for a charge summing
// several costs, each cost with its own figure and the total beneath.
export function amountTex(event, currency) {
  const amount = result(texMoney(event.amount, currency));
  if (event.formula.length === 1) return `${productTex(event.formula[0], currency)} = ${amount}`;
  const rows = event.formula.map(
    (p) =>
      `${text(p.label ?? '')} & ${productTex(p, currency)} & = ${texMoney(p.value, currency)}`,
  );
  const total = `\\rule{0pt}{1.6em}${text('Total')} & & = ${amount}`;
  return `\\begin{array}{lcr} ${rows.join(' \\\\[2.4em] ')} \\\\ \\hline ${total} \\end{array}`;
}

const signed = (value, currency) => `${Number(value) < 0 ? '-' : ''}${texMoney(value, currency)}`;

// The running balance after an event: the one before it, with this event's cash flow added or
// taken away. Before the first event the balance is nothing.
export function netCashTex(event, previous, currency) {
  const before = previous
    ? braced(signed(previous.balance, currency), `Net cash after event ${previous.number}`)
    : braced(texMoney(0, currency), 'Opening net cash');
  const sign = Number(event.amount) < 0 ? '-' : '+';
  const flow = braced(texMoney(event.amount, currency), event.name);
  return `${before} ${sign} ${flow} = ${result(signed(event.balance, currency))}`;
}
